/**
 * Route poster v2 (templates/product.route-poster-v2.json): the page state the
 * v2 sections share. Exposes window.TNRP2.
 *
 *   - Package: free | priority | framed, and the variant it maps to. Picked in
 *     sections/tn-rp2-packages, refined in sections/tn-rp2-frames, read by the
 *     trip form (assets/tn-rp2-form.js). Changes fire `tn-rp2:change`.
 *   - Draft time and the cutoff line: worked out from the real clock in the
 *     shop's time zone and the cutoff settings, so a reload never restarts it.
 *   - The "Ask the designer" WhatsApp dialog (sections/tn-rp2-ask).
 *
 * Config comes from <script type="application/json" data-tn-rp2-config> tags
 * the sections print; prices are the variants' own, never typed.
 *
 * Plain ES2019, no dependencies.
 */
(function () {
  if (window.TNRP2) return;

  var DAY = 86400000;
  var DEFAULT_TZ = 'Asia/Kolkata';

  var config = { variants: {}, packages: {}, frames: [], cutoff: {}, freeDays: 90, defaultPackage: 'priority' };
  var state = { pkg: null, variantId: null, frameVariantId: null };

  /* ------------------------------------------------------------------ */
  /* Config                                                              */
  /* ------------------------------------------------------------------ */

  function merge(src) {
    if (!src || typeof src !== 'object') return;
    Object.keys(src).forEach(function (key) {
      var value = src[key];
      if (key === 'variants' || key === 'packages' || key === 'cutoff') {
        config[key] = Object.assign({}, config[key], value);
      } else if (key === 'frames' && Array.isArray(value)) {
        config.frames = value;
      } else {
        config[key] = value;
      }
    });
  }

  function readConfig() {
    document.querySelectorAll('script[data-tn-rp2-config]').forEach(function (tag) {
      try {
        merge(JSON.parse(tag.textContent));
      } catch (e) {
        /* a broken tag never breaks the page */
      }
    });
  }

  function variant(id) {
    if (id === null || id === undefined || id === '') return null;
    return config.variants[String(id)] || null;
  }

  function isFreeVariant(id) {
    return !!config.packages.free && String(id) === String(config.packages.free);
  }

  function frameIds() {
    return config.frames
      .map(function (frame) {
        return String(frame.variantId);
      })
      .filter(function (id) {
        return !!variant(id);
      });
  }

  function firstFrame() {
    var ids = frameIds();
    for (var i = 0; i < ids.length; i++) {
      if (variant(ids[i]).available) return ids[i];
    }
    return ids[0] || null;
  }

  /* True while at least one frame tile can be bought. */
  function framesAvailable() {
    return frameIds().some(function (id) {
      return variant(id).available;
    });
  }

  /* The lowest frame price among the frame tiles (available ones first). */
  function lowestFrame() {
    var ids = frameIds();
    var pool = ids.filter(function (id) {
      return variant(id).available;
    });
    if (!pool.length) pool = ids;
    var best = null;
    pool.forEach(function (id) {
      var v = variant(id);
      if (!best || v.price < best.price) best = v;
    });
    return best;
  }

  /* ------------------------------------------------------------------ */
  /* Time: draft time and cutoff                                         */
  /* ------------------------------------------------------------------ */

  function parseHM(text, fallback) {
    var match = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(text || '');
    if (!match) return fallback;
    var h = Number(match[1]);
    var m = Number(match[2]);
    if (h > 23 || m > 59) return fallback;
    return h * 60 + m;
  }

  /** Today's date (as a UTC midnight timestamp) and minutes since midnight, in the shop's zone. */
  function zonedNow(tz) {
    var now = new Date();
    var zones = [tz, DEFAULT_TZ];
    for (var i = 0; i < zones.length; i++) {
      try {
        var parts = {};
        new Intl.DateTimeFormat('en-GB', {
          timeZone: zones[i],
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        })
          .formatToParts(now)
          .forEach(function (part) {
            parts[part.type] = part.value;
          });
        var hour = Number(parts.hour) % 24;
        return {
          day: Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)),
          minutes: hour * 60 + Number(parts.minute) + Number(parts.second) / 60,
        };
      } catch (e) {
        /* unknown zone: try the next */
      }
    }
    return {
      day: Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
      minutes: now.getHours() * 60 + now.getMinutes(),
    };
  }

  function isoDay(dayMs) {
    return new Date(dayMs).toISOString().slice(0, 10);
  }

  function cutoffRules() {
    var c = config.cutoff || {};
    return {
      tz: c.timezone || DEFAULT_TZ,
      start: parseHM(c.workStart, 10 * 60),
      end: parseHM(c.workEnd, 17 * 60),
      draft: Math.max(0, Number(c.draftHours === undefined ? 4 : c.draftHours) || 0) * 60,
      days: Array.isArray(c.workingDays) ? c.workingDays.map(Number) : [1, 2, 3, 4, 5],
      closed: Array.isArray(c.closedDates) ? c.closedDates.map(String) : [],
      today: c.todayWord || 'today',
      tomorrow: c.tomorrowWord || 'tomorrow',
    };
  }

  function isWorking(dayMs, rules) {
    return rules.days.indexOf(new Date(dayMs).getUTCDay()) !== -1 && rules.closed.indexOf(isoDay(dayMs)) === -1;
  }

  function nextWorking(dayMs, rules) {
    for (var i = 1; i <= 90; i++) {
      if (isWorking(dayMs + i * DAY, rules)) return dayMs + i * DAY;
    }
    return dayMs + DAY;
  }

  function timeLabel(minutes) {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    var suffix = h < 12 ? 'AM' : 'PM';
    var h12 = h % 12 || 12;
    return (m ? h12 + ':' + (m < 10 ? '0' : '') + m : String(h12)) + ' ' + suffix;
  }

  function dayLabel(dayMs, todayMs, rules) {
    var diff = Math.round((dayMs - todayMs) / DAY);
    if (diff === 0) return rules.today;
    if (diff === 1) return rules.tomorrow;
    var options = diff < 7 ? { weekday: 'long' } : { weekday: 'long', day: 'numeric', month: 'long' };
    options.timeZone = 'UTC';
    try {
      return new Intl.DateTimeFormat('en-IN', options).format(new Date(dayMs));
    } catch (e) {
      return isoDay(dayMs);
    }
  }

  /**
   * When a booking made now gets its draft.
   *   A draft takes `draft` minutes of studio time (working days, start to
   *   end), counted from now, or from the next opening outside those hours.
   *   What is left at closing carries into the next working day.
   *   The time shown is rounded up to the half hour.
   * The countdown runs to the last booking whose draft lands today.
   * Same rules as the Studio's due dates (studio-toponomad
   * src/lib/intake/draft-due.ts): change one, change the other.
   */
  function draftInfo() {
    var rules = cutoffRules();
    if (!rules.days.length || rules.end <= rules.start) return null;
    var now = zonedNow(rules.tz);
    var day = now.day;
    var at = Math.max(now.minutes, rules.start);
    if (!isWorking(day, rules) || now.minutes >= rules.end) {
      day = nextWorking(day, rules);
      at = rules.start;
    }
    var left = rules.draft;
    while (left > rules.end - at) {
      left -= rules.end - at;
      day = nextWorking(day, rules);
      at = rules.start;
    }
    var ready = Math.ceil((at + left) / 30) * 30;
    var lastSameDay = rules.end - rules.draft;
    var before = isWorking(now.day, rules) && now.minutes < lastSameDay;
    return {
      before: before,
      minutesLeft: before ? Math.max(1, Math.ceil(lastSameDay - now.minutes)) : 0,
      label: dayLabel(day, now.day, rules) + ', ' + timeLabel(ready),
    };
  }

  function countdownText(minutes) {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    return h ? h + 'h ' + m + 'm' : m + 'm';
  }

  function refreshCutoff() {
    var info = draftInfo();
    if (info) {
      document.querySelectorAll('[data-tn-rp2-draft-time]').forEach(function (el) {
        if (el.textContent !== info.label) el.textContent = info.label;
      });
    }
    document.querySelectorAll('[data-tn-rp2-cutoff]').forEach(function (el) {
      var out = el.querySelector('[data-tn-rp2-cutoff-text]');
      var template = info ? (info.before ? el.dataset.before : el.dataset.after) : '';
      if (!info || !template || !out || state.pkg === 'free') {
        el.hidden = true;
        return;
      }
      out.textContent = template
        .replace('[countdown]', countdownText(info.minutesLeft))
        .replace('[draft_time]', info.label);
      el.hidden = false;
    });
  }

  /** Whole days between a YYYY-MM-DD date and today in the shop's zone (positive = past). */
  function daysAgo(isoDate) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || '');
    if (!match) return 0;
    var then = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Math.round((zonedNow(cutoffRules().tz).day - then) / DAY);
  }

  /* ------------------------------------------------------------------ */
  /* Package and frame                                                   */
  /* ------------------------------------------------------------------ */

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* The trip form asks first and offers the packages after (RP2 trip form "Flow" setting). */
  function optionsAfter() {
    return !!document.querySelector('tn-rp2-form[data-flow="options_after"]');
  }

  function scrollTo(target) {
    if (optionsAfter()) target = 'form';
    var el = document.getElementById(target === 'frames' ? 'tn-rp2-frames' : 'tn-rp2-form');
    if (!el || el.hidden) el = document.getElementById('tn-rp2-form');
    if (!el) return;
    el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  function updateUrl(variantId) {
    if (!variantId || !window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) {
      /* the page works without it */
    }
  }

  function announce() {
    syncUi();
    document.dispatchEvent(
      new CustomEvent('tn-rp2:change', { detail: { pkg: state.pkg, variantId: state.variantId } })
    );
  }

  function selectPackage(pkg, options) {
    options = options || {};
    var corrected = false;
    if (pkg !== 'free' && pkg !== 'priority' && pkg !== 'framed') pkg = 'priority';
    /* No frame to buy: a link or default asking for Framed opens on Priority instead. */
    if (pkg === 'framed' && !framesAvailable()) {
      pkg = 'priority';
      corrected = true;
    }
    state.pkg = pkg;
    if (pkg === 'free') state.variantId = config.packages.free || null;
    else if (pkg === 'priority') state.variantId = config.packages.priority || null;
    else {
      /* A remembered frame that has since sold out gives way to one in stock. */
      var kept = variant(state.frameVariantId);
      state.variantId = kept && kept.available ? state.frameVariantId : firstFrame();
      if (state.frameVariantId && state.variantId !== state.frameVariantId) corrected = true;
    }
    if (pkg === 'framed' && state.variantId) state.frameVariantId = state.variantId;
    /* The link is corrected too when it asked for something that could not be picked. */
    if (options.url !== false || corrected) updateUrl(state.variantId);
    announce();
    if (options.scroll) scrollTo(pkg === 'framed' ? 'frames' : 'form');
  }

  function selectFrame(variantId) {
    state.frameVariantId = String(variantId);
    state.pkg = 'framed';
    state.variantId = state.frameVariantId;
    updateUrl(state.variantId);
    announce();
  }

  function syncUi() {
    document.querySelectorAll('[data-tn-rp2-package]').forEach(function (card) {
      card.classList.toggle('is-selected', card.getAttribute('data-tn-rp2-package') === state.pkg);
    });
    document.querySelectorAll('[data-tn-rp2-choose]').forEach(function (button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-tn-rp2-choose') === state.pkg ? 'true' : 'false');
    });

    /* The Framed card follows the frame tiles' stock (the Liquid only guessed from all variants). */
    var framesSoldOut = !framesAvailable();
    document.querySelectorAll('[data-tn-rp2-choose="framed"]').forEach(function (button) {
      var card = button.closest('[data-tn-rp2-package]');
      if (card) card.classList.toggle('is-sold-out', framesSoldOut);
      button.disabled = framesSoldOut;
      var label = framesSoldOut ? button.dataset.soldOutLabel : button.dataset.label;
      if (label && button.textContent !== label) button.textContent = label;
    });

    var designMode = !!(window.Shopify && window.Shopify.designMode);
    document.querySelectorAll('[data-tn-rp2-frames]').forEach(function (section) {
      /* Form-first flow: frames are picked on the options screen instead. */
      section.hidden = optionsAfter() || (!designMode && state.pkg !== 'framed');
      var chosen = state.pkg === 'framed' ? String(state.frameVariantId || '') : '';
      section.querySelectorAll('[data-tn-rp2-frame]').forEach(function (tile) {
        var on = tile.getAttribute('data-tn-rp2-frame') === chosen;
        tile.classList.toggle('is-selected', on);
        tile.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      var out = section.querySelector('[data-tn-rp2-frame-detail-out]');
      var detail = chosen ? section.querySelector('[data-tn-rp2-frame-detail="' + chosen + '"]') : null;
      if (out) {
        out.textContent = '';
        if (detail && detail.content) out.appendChild(detail.content.cloneNode(true));
      }
    });

    /* Prices from the variants: the Priority booking, and "From" the lowest frame. */
    var priority = variant(config.packages.priority);
    var lowest = lowestFrame();
    document.querySelectorAll('[data-tn-rp2-price]').forEach(function (el) {
      var kind = el.getAttribute('data-tn-rp2-price');
      if (kind === 'priority' && priority) el.textContent = priority.priceText;
      if (kind === 'framed' && lowest) el.textContent = lowest.priceText;
    });

    refreshCutoff();
  }

  function initialPackage() {
    var params;
    try {
      params = new URL(window.location.href).searchParams;
    } catch (e) {
      params = null;
    }
    var fromUrl = params ? params.get('variant') : null;
    if (fromUrl) {
      if (isFreeVariant(fromUrl)) return selectPackage('free', { url: false });
      if (config.packages.priority && String(fromUrl) === String(config.packages.priority)) {
        return selectPackage('priority', { url: false });
      }
      if (frameIds().indexOf(String(fromUrl)) !== -1) {
        state.frameVariantId = String(fromUrl);
        return selectPackage('framed', { url: false });
      }
    }
    var asked = params ? params.get('package') : null;
    selectPackage(asked || config.defaultPackage || 'priority', { url: false });
  }

  /* ------------------------------------------------------------------ */
  /* Ask the designer (WhatsApp)                                         */
  /* ------------------------------------------------------------------ */

  var ask = {
    root: null,
    lastFocus: null,

    init: function () {
      this.root = document.querySelector('[data-tn-rp2-ask]');
      if (!this.root || this.root.dataset.ready) return;
      this.root.dataset.ready = 'true';
      this.dialog = this.root.querySelector('[data-tn-rp2-ask-dialog]');
      this.overlay = this.root.querySelector('[data-tn-rp2-ask-overlay]');
      this.text = this.root.querySelector('[data-tn-rp2-ask-text]');
      this.send = this.root.querySelector('[data-tn-rp2-ask-send]');
      this.min = Math.max(1, Number(this.root.dataset.min) || 5);
      if (!this.dialog || !this.text || !this.send) return;

      var self = this;
      this.text.addEventListener('input', function () {
        self.send.disabled = self.text.value.trim().length < self.min;
      });
      this.send.addEventListener('click', function () {
        self.sendMessage();
      });
      this.root.querySelectorAll('[data-tn-rp2-ask-close]').forEach(function (button) {
        button.addEventListener('click', function () {
          self.close();
        });
      });
      if (this.overlay) {
        this.overlay.addEventListener('click', function () {
          self.close();
        });
      }
      this.dialog.addEventListener('keydown', function (event) {
        self.onKeydown(event);
      });
    },

    focusables: function () {
      return Array.prototype.slice
        .call(this.dialog.querySelectorAll('button, textarea, a[href], input, [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) {
          return !el.disabled && !el.hidden;
        });
    },

    open: function (trigger) {
      if (!this.dialog) return;
      this.lastFocus = trigger || document.activeElement;
      if (this.overlay) this.overlay.hidden = false;
      this.dialog.hidden = false;
      document.documentElement.classList.add('tn-rp2-no-scroll');
      this.send.disabled = this.text.value.trim().length < this.min;
      this.text.focus();
    },

    close: function () {
      if (!this.dialog || this.dialog.hidden) return;
      this.dialog.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
      document.documentElement.classList.remove('tn-rp2-no-scroll');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },

    onKeydown: function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      var items = this.focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    sendMessage: function () {
      var question = this.text.value.trim();
      if (question.length < this.min) return;
      var number = (this.root.dataset.number || '').replace(/\D/g, '');
      var title = this.root.dataset.productTitle || (config.product && config.product.title) || document.title;
      var intro = (this.root.dataset.intro || "Hi, I'm looking at [product].").replace('[product]', title);
      var message = intro + ' ' + question + '\n\n' + window.location.href;
      window.open('https://wa.me/' + number + '?text=' + encodeURIComponent(message), '_blank', 'noopener');
      this.close();
    },
  };

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var choose = target.closest('[data-tn-rp2-choose]');
    if (choose) {
      selectPackage(choose.getAttribute('data-tn-rp2-choose'), { scroll: true });
      return;
    }
    var frame = target.closest('[data-tn-rp2-frame]');
    if (frame) {
      if (frame.getAttribute('aria-disabled') === 'true') return;
      selectFrame(frame.getAttribute('data-tn-rp2-frame'));
      return;
    }
    if (target.closest('[data-tn-rp2-to-form]')) {
      scrollTo('form');
      return;
    }
    var askOpen = target.closest('[data-tn-rp2-ask-open]');
    if (askOpen) {
      ask.init();
      ask.open(askOpen);
    }
  });

  /* The frame comparison folds away on phones only. */
  function openCompareOnDesktop() {
    if (!window.matchMedia || !window.matchMedia('(min-width: 750px)').matches) return;
    document.querySelectorAll('[data-tn-rp2-compare]').forEach(function (details) {
      details.open = true;
    });
  }

  /**
   * The green draft-time chip at the top of the product details stays in
   * view: once it scrolls up under the header, a copy pins just below the
   * header. The copy carries data-tn-rp2-cutoff, so refreshCutoff keeps its
   * text and its hidden state (Free preview) in step with the original.
   */
  function stickyDraftLine() {
    var pill = document.querySelector('.tn-rp2-draft-line [data-tn-rp2-cutoff]');
    if (!pill || document.querySelector('.tn-rp2-draft-sticky')) return;
    var bar = document.createElement('div');
    bar.className = 'tn-rp2-draft-sticky';
    bar.setAttribute('aria-hidden', 'true');
    var line = document.createElement('div');
    line.className = 'tn-rp2-draft-line';
    line.appendChild(pill.cloneNode(true));
    bar.appendChild(line);
    document.body.appendChild(bar);
    refreshCutoff();

    var header = document.querySelector('#header-component');
    var queued = false;
    function update() {
      queued = false;
      var top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      var past = !pill.hidden && pill.getBoundingClientRect().bottom < top;
      bar.style.top = top + 'px';
      bar.classList.toggle('is-visible', past);
    }
    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }

  function init() {
    readConfig();
    openCompareOnDesktop();
    ask.init();
    initialPackage();
    stickyDraftLine();
    document.dispatchEvent(new CustomEvent('tn-rp2:ready'));
    /* Keeps the countdown and the draft day true while the page stays open. */
    setInterval(refreshCutoff, 20000);
  }

  /* Theme editor: a re-rendered section brings a fresh config tag. */
  document.addEventListener('shopify:section:load', function () {
    readConfig();
    ask.init();
    syncUi();
  });

  window.TNRP2 = {
    config: config,
    state: state,
    variant: variant,
    isFreeVariant: isFreeVariant,
    framesAvailable: framesAvailable,
    selectPackage: selectPackage,
    selectFrame: selectFrame,
    scrollTo: scrollTo,
    refreshCutoff: refreshCutoff,
    draftInfo: draftInfo,
    daysAgo: daysAgo,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
